import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as L from 'leaflet';
import { Feature, GeoJsonObject, GeoJsonProperties, Geometry } from 'geojson';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  ContactAutocompleteResult,
  ContactAutocompleteService
} from '../../services/contacts/contact-autocomplete.service';
import {
  ZoneMakerApiService,
  ZoneMakerBoundaryCountry,
  ZoneMakerFeatureCollection,
  ZoneMakerRadiusRequest,
  ZoneMakerStateOption,
  ZoneMakerZoneSummary,
  ZoneMakerZoneType
} from './zone-maker-api.service';

type GeoJsonLeafletLayer = L.Layer & {
  feature?: Feature<Geometry, GeoJsonProperties>;
};

type PostalMergeMode = 'add' | 'replace';

interface ZoneMakerLastContact {
  zoneType: ZoneMakerZoneType;
  contactUuid: string;
  contactLabel: string;
  country: ZoneMakerBoundaryCountry;
}

@Component({
  selector: 'app-zone-maker',
  templateUrl: './zone-maker.component.html',
  styleUrls: ['./zone-maker.component.scss']
})
export class ZoneMakerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapBox', { static: false }) mapBox?: ElementRef<HTMLDivElement>;

  readonly uuidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  readonly metersPerMile = 1609.344;
  readonly zonePalette = [
    '#38bdf8', '#34d399', '#fbbf24', '#fb7185', '#a78bfa', '#f97316', '#22c55e', '#06b6d4',
    '#e879f9', '#84cc16', '#60a5fa', '#f43f5e', '#14b8a6', '#eab308', '#c084fc', '#ef4444',
    '#2dd4bf', '#a3e635', '#818cf8', '#f59e0b', '#10b981', '#ec4899', '#0ea5e9', '#d946ef',
    '#facc15', '#4ade80', '#fb923c', '#8b5cf6', '#22d3ee', '#be123c', '#65a30d', '#2563eb'
  ];
  readonly sidebarMinWidth = 340;
  readonly sidebarMaxWidth = 620;
  private readonly lastContactStorageKey = 'zoneMaker:lastContact';

  sidebarWidth = 410;
  resizingSidebar = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private zipLoadRequestId = 0;

  boundaryCountry: ZoneMakerBoundaryCountry = 'USA';
  zoneType: ZoneMakerZoneType = 'customer';
  contactSearchTerm = '';
  uuidDraft = '';
  selectedContactUuid = '';
  selectedContact: ContactAutocompleteResult | null = null;
  contactOptions: ContactAutocompleteResult[] = [];
  contactLoading = false;
  contactError = '';

  zipSearchTerm = '';
  zipSearchResults: { postal_code: string; state_abbr: string; state_name: string; center_lat: string | number; center_lng: string | number }[] = [];
  zipSearchLoading = false;

  stateOptions: ZoneMakerStateOption[] = [];
  selectedStates = new Set<string>();
  selectedStateList: string[] = [];
  statesLoading = false;

  existingZones: ZoneMakerZoneSummary[] = [];
  existingZonesLoading = false;
  zoneLegendCollapsed = false;
  editingZoneUuid = '';

  zoneName = '';
  zoneId = '';
  country = 'USA';
  description = '';
  boundaryCleanupEnabled = true;
  gapFillMiles = 0.25;
  cleanHoleSquareMiles = 0.25;

  radiusCenterZip = '';
  radiusMiles = 25;
  radiusMergeMode: PostalMergeMode = 'add';
  radiusLoading = false;

  selectedZips = new Set<string>();
  selectedZipList: string[] = [];
  loadedZipCodes: string[] = [];
  loadedZipCount = 0;
  zipLimit = 2500;
  loadingZips = false;
  zipsLimited = false;

  previewLoading = false;
  saving = false;
  previewStats: { postal_code_count: number; square_miles: number | null } | null = null;

  private map?: L.Map;
  private zipLayer?: L.GeoJSON;
  private existingZonesLayer?: L.GeoJSON;
  private activeZoneLayer?: L.GeoJSON;
  private radiusSelectionLayer?: L.GeoJSON;
  private radiusCircle?: L.Circle;
  private focusZipLayer?: L.GeoJSON;
  private previewLayer?: L.GeoJSON;
  private readonly existingZoneColors = new Map<string, string>();

  private readonly subscriptions = new Subscription();
  private readonly contactSearch$ = new Subject<string>();
  private readonly zipSearch$ = new Subject<string>();
  private readonly mapMove$ = new Subject<void>();

  constructor(
    private readonly api: ZoneMakerApiService,
    private readonly contactAutocomplete: ContactAutocompleteService,
    private readonly toastr: ToastrService
  ) {}

  @HostListener('document:mousemove', ['$event'])
  onSidebarResizeMove(event: MouseEvent): void {
    if (!this.resizingSidebar) {
      return;
    }

    const width = this.resizeStartWidth + event.clientX - this.resizeStartX;
    this.sidebarWidth = Math.min(this.sidebarMaxWidth, Math.max(this.sidebarMinWidth, width));
    this.map?.invalidateSize();
  }

  @HostListener('document:mouseup')
  onSidebarResizeEnd(): void {
    if (!this.resizingSidebar) {
      return;
    }

    this.resizingSidebar = false;
    this.map?.invalidateSize();
  }

  onSidebarResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.resizingSidebar = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.sidebarWidth;
  }

  ngOnInit(): void {
    this.restoreLastContact();
    this.loadStates();

    this.subscriptions.add(
      this.contactSearch$.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(query => {
          this.contactError = '';
          this.contactLoading = query.trim().length >= 3;
        }),
        switchMap(query => {
          const cleanQuery = query.trim();
          if (cleanQuery.length < 3) {
            this.contactLoading = false;
            return of([]);
          }

          return this.contactAutocomplete.search(this.contactAutocompleteType, cleanQuery).pipe(
            catchError(() => {
              this.contactError = 'Contact search failed';
              return of([]);
            })
          );
        })
      ).subscribe(options => {
        this.contactOptions = options;
        this.contactLoading = false;
      })
    );

    this.subscriptions.add(
      this.zipSearch$.pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap(query => {
          this.zipSearchLoading = query.trim().length >= 2;
        }),
        switchMap(query => {
          const cleanQuery = query.trim();
          if (cleanQuery.length < 2) {
            this.zipSearchLoading = false;
            return of([]);
          }

          return this.api.searchZips(cleanQuery, this.boundaryCountry).pipe(
            map(response => response.data.results),
            catchError(() => {
              this.toastr.error(this.boundaryCodeLabel + ' search failed');
              return of([]);
            })
          );
        })
      ).subscribe(results => {
        this.zipSearchResults = results;
        this.zipSearchLoading = false;
      })
    );

    this.subscriptions.add(
      this.mapMove$.pipe(debounceTime(250)).subscribe(() => {
        this.loadViewportZips();
      })
    );
  }

  ngAfterViewInit(): void {
    if (!this.mapBox) {
      return;
    }

    this.map = L.map(this.mapBox.nativeElement, {
      center: this.boundaryCountry === 'CAN' ? [56.1304, -106.3468] : [39.5, -98.35],
      zoom: this.boundaryCountry === 'CAN' ? 4 : 5,
      preferCanvas: true
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    this.zipLayer = L.geoJSON(undefined, {
      style: feature => this.zipStyle(feature),
      onEachFeature: (feature, layer) => this.bindZipFeature(feature, layer)
    }).addTo(this.map);

    this.map.on('moveend zoomend', () => this.mapMove$.next());
    this.map.on('click', event => this.onMapClick(event.latlng));

    window.setTimeout(() => {
      this.map?.invalidateSize();
      this.loadViewportZips();
      if (this.selectedContactUuid) {
        this.loadExistingZones(true);
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.map?.remove();
  }

  get contactAutocompleteType(): string {
    return this.zoneType === 'carrier' ? 'carrier_contact_list' : 'customer_contact_list';
  }

  get contactScopeLabel(): string {
    return this.zoneType === 'carrier' ? 'Carrier' : 'Customer';
  }

  get selectedContactLabel(): string {
    if (this.selectedContact) {
      return this.selectedContact.label;
    }

    return this.selectedContactUuid || 'No contact selected';
  }

  get isEditingZone(): boolean {
    return this.editingZoneUuid !== '';
  }

  get canSave(): boolean {
    return this.selectedContactUuid !== ''
      && this.zoneName.trim() !== ''
      && this.selectedZipList.length > 0
      && !this.saving;
  }

  get visibleSelectedZipList(): string[] {
    return this.selectedZipList.slice(0, 160);
  }

  get boundaryCountryLabel(): string {
    return this.boundaryCountry === 'CAN' ? 'Canada' : 'USA';
  }

  get boundaryCodeLabel(): string {
    return this.boundaryCountry === 'CAN' ? 'FSA' : 'ZIP';
  }

  get boundaryCodePluralLabel(): string {
    return this.boundaryCountry === 'CAN' ? 'FSAs' : 'ZIPs';
  }

  get regionTitle(): string {
    return this.boundaryCountry === 'CAN' ? 'Provinces' : 'States';
  }

  get regionPluralLabel(): string {
    return this.boundaryCountry === 'CAN' ? 'provinces' : 'states';
  }

  get selectedStateSummary(): string {
    if (this.selectedStateList.length === 0) {
      return 'No ' + this.regionPluralLabel;
    }

    if (this.selectedStateList.length <= 8) {
      return this.selectedStateList.join(', ');
    }

    return this.selectedStateList.slice(0, 8).join(', ') + ' +' + (this.selectedStateList.length - 8);
  }

  onBoundaryCountryChange(country: ZoneMakerBoundaryCountry): void {
    if (this.boundaryCountry === country) {
      return;
    }

    this.setBoundaryCountry(country, true);
    this.startNewZone();
    this.persistLastContact();
  }

  onZoneTypeChange(zoneType: ZoneMakerZoneType): void {
    if (this.zoneType === zoneType) {
      return;
    }

    this.zoneType = zoneType;
    this.resetContact();
    this.clearZoneWorkspace();
  }

  onContactSearchChange(value: string): void {
    this.contactSearchTerm = value;
    this.selectedContact = null;
    this.contactSearch$.next(value);
  }

  selectContact(option: ContactAutocompleteResult): void {
    this.selectedContact = option;
    this.selectedContactUuid = option.value;
    this.uuidDraft = option.value;
    this.contactSearchTerm = option.label;
    this.contactOptions = [];
    this.persistLastContact();
    this.clearZoneWorkspace();
    this.loadExistingZones(true);
  }

  applyUuidDraft(): void {
    const uuid = this.uuidDraft.trim();
    if (!this.uuidPattern.test(uuid)) {
      this.toastr.error('Enter a valid contact UUID');
      return;
    }

    this.selectedContact = null;
    this.selectedContactUuid = uuid.toLowerCase();
    this.contactSearchTerm = '';
    this.contactOptions = [];
    this.persistLastContact();
    this.clearZoneWorkspace();
    this.loadExistingZones(true);
  }

  onZipSearchChange(value: string): void {
    this.zipSearchTerm = value;
    this.zipSearch$.next(value);
  }

  loadStates(): void {
    const country = this.boundaryCountry;
    this.statesLoading = true;
    this.api.getStates(country).subscribe({
      next: response => {
        if (country !== this.boundaryCountry) {
          return;
        }

        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.stateOptions = response.data.states;
      },
      error: () => this.toastr.error(this.regionTitle + ' failed to load'),
      complete: () => {
        if (country === this.boundaryCountry) {
          this.statesLoading = false;
        }
      }
    });
  }

  toggleState(stateAbbr: string): void {
    const state = stateAbbr.toUpperCase();
    if (this.selectedStates.has(state)) {
      this.selectedStates.delete(state);
    } else {
      this.selectedStates.add(state);
    }

    this.syncSelectedStateList();
    this.loadViewportZips();
  }

  selectAllStates(): void {
    this.stateOptions.forEach(state => this.selectedStates.add(state.state_abbr));
    this.syncSelectedStateList();
    this.loadViewportZips();
  }

  clearStates(): void {
    this.selectedStates.clear();
    this.syncSelectedStateList();
    this.loadedZipCodes = [];
    this.loadedZipCount = 0;
    this.zipsLimited = false;
    this.zipLayer?.clearLayers();
  }

  isStateSelected(stateAbbr: string): boolean {
    return this.selectedStates.has(stateAbbr);
  }

  zoneColorForName(name: string | null): string {
    return this.colorForExistingZoneKeys(null, name);
  }

  zoneColorForZone(zone: ZoneMakerZoneSummary): string {
    return this.colorForExistingZoneKeys(zone.zone_uuid, zone.zone_name);
  }

  toggleZoneLegendCollapsed(): void {
    this.zoneLegendCollapsed = !this.zoneLegendCollapsed;
  }

  focusZip(postalCode: string, selectZip = true): void {
    const cleanPostalCode = this.normalizePostalCode(postalCode);
    if (!cleanPostalCode) {
      this.toastr.error('Enter a valid ' + this.boundaryCodeLabel);
      return;
    }

    this.api.getZip(cleanPostalCode, this.currentZoom(), this.boundaryCountry).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.drawFocusZip(response.data.feature_collection);
        if (selectZip) {
          this.mergePostalCodes([response.data.postal_code], 'add');
        }

        const center = L.latLng(response.data.center_lat, response.data.center_lng);
        this.map?.setView(center, Math.max(this.currentZoom(), 10));
        this.zipSearchResults = [];
        this.zipSearchTerm = response.data.postal_code;
      },
      error: () => this.toastr.error(this.boundaryCodeLabel + ' lookup failed')
    });
  }

  loadExistingZones(fitBounds = false): void {
    if (!this.selectedContactUuid) {
      return;
    }

    this.existingZonesLoading = true;
    this.api.getZones(this.zoneType, this.selectedContactUuid, this.existingZonesOverviewZoom()).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.existingZones = response.data.zones;
        this.syncExistingZoneColors();
        this.drawExistingZones(response.data.feature_collection, fitBounds);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Existing zones failed to load')),
      complete: () => {
        this.existingZonesLoading = false;
      }
    });
  }

  loadZoneForEditing(zone: ZoneMakerZoneSummary): void {
    if (!this.selectedContactUuid) {
      return;
    }

    this.api.getZone(this.zoneType, this.selectedContactUuid, zone.zone_uuid, this.currentZoom()).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        const detail = response.data.zone;
        this.editingZoneUuid = detail.zone_uuid;
        this.zoneName = detail.zone_name;
        this.zoneId = detail.zone_id || '';
        const detailCountry = this.toBoundaryCountry(detail.country);
        if (detailCountry && detailCountry !== this.boundaryCountry) {
          this.setBoundaryCountry(detailCountry, false);
        }

        this.country = detailCountry || this.boundaryCountry;
        this.description = detail.description || '';
        this.applyLoadedZoneRegions(detail.region_codes || []);
        this.mergePostalCodes(detail.postal_codes, 'replace');
        this.drawActiveZone(response.data.feature_collection, true, detail.zone_name);
        this.zoneLegendCollapsed = true;
        this.previewStats = null;
        window.setTimeout(() => this.loadViewportZips(), 0);
        this.toastr.info(`${detail.zone_name} selected for editing with ${detail.postal_codes.length} ${this.boundaryCodePluralLabel}`);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Zone details failed to load'))
    });
  }

  startNewZone(): void {
    this.editingZoneUuid = '';
    this.zoneName = '';
    this.zoneId = '';
    this.country = this.boundaryCountry;
    this.description = '';
    this.removeLayer('activeZoneLayer');
    this.clearSelection();
  }

  selectVisibleZips(): void {
    this.mergePostalCodes(this.loadedZipCodes, 'add');
  }

  removeVisibleZips(): void {
    this.loadedZipCodes.forEach(postalCode => this.selectedZips.delete(postalCode));
    this.syncSelectedZipList();
    this.refreshZipLayerStyles();
    this.previewStats = null;
  }

  clearSelection(): void {
    this.selectedZips.clear();
    this.syncSelectedZipList();
    this.refreshZipLayerStyles();
    this.previewStats = null;
    this.removeLayer('radiusSelectionLayer');
    this.removeLayer('previewLayer');
    this.radiusCircle?.remove();
    this.radiusCircle = undefined;
  }

  removeSelectedZip(postalCode: string): void {
    this.selectedZips.delete(postalCode);
    this.syncSelectedZipList();
    this.refreshZipLayerStyles();
    this.previewStats = null;
  }

  setBoundaryCleanupEnabled(enabled: boolean): void {
    if (this.boundaryCleanupEnabled === enabled) {
      return;
    }

    this.boundaryCleanupEnabled = enabled;
    this.previewStats = null;
    this.removeLayer('previewLayer');
  }

  onGapFillMilesChange(value: string | number): void {
    const miles = Number(value);
    this.gapFillMiles = Number.isFinite(miles) ? Math.min(5, Math.max(0, miles)) : 0;
    this.previewStats = null;
    this.removeLayer('previewLayer');
  }

  onCleanHoleSquareMilesChange(value: string | number): void {
    const squareMiles = Number(value);
    this.cleanHoleSquareMiles = Number.isFinite(squareMiles) ? Math.min(100, Math.max(0, squareMiles)) : 0;
    this.previewStats = null;
    this.removeLayer('previewLayer');
  }


  applyRadiusFromZip(): void {
    if (this.selectedStateList.length === 0) {
      this.toastr.error('Select at least one ' + this.regionPluralLabel.slice(0, -1) + ' first');
      return;
    }

    const postalCode = this.normalizePostalCode(this.radiusCenterZip);
    if (!postalCode) {
      this.toastr.error('Enter a valid center ' + this.boundaryCodeLabel);
      return;
    }

    this.applyRadius({
      center_postal_code: postalCode,
      radius_miles: this.radiusMiles,
      zoom: this.currentZoom(),
      states: this.selectedStateList,
      country: this.boundaryCountry
    });
  }

  applyRadiusFromMapCenter(): void {
    if (this.selectedStateList.length === 0) {
      this.toastr.error('Select at least one ' + this.regionPluralLabel.slice(0, -1) + ' first');
      return;
    }

    const center = this.map?.getCenter();
    if (!center) {
      return;
    }

    this.applyRadius({
      lat: center.lat,
      lng: center.lng,
      radius_miles: this.radiusMiles,
      zoom: this.currentZoom(),
      states: this.selectedStateList,
      country: this.boundaryCountry
    });
  }

  previewSelection(): void {
    if (this.selectedZipList.length === 0) {
      this.toastr.error('Select ' + this.boundaryCodePluralLabel + ' first');
      return;
    }

    this.previewLoading = true;
    this.api.preview(this.selectedZipList, this.currentZoom(), this.boundaryCountry, this.normalizedGapFillMiles(), this.normalizedCleanHoleSquareMiles()).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.previewStats = {
          postal_code_count: response.data.postal_code_count,
          square_miles: response.data.square_miles
        };
        this.drawPreview(response.data.feature_collection, true);
      },
      error: error => this.toastr.error(this.apiErrorMessage(error, 'Preview failed')),
      complete: () => {
        this.previewLoading = false;
      }
    });
  }

  saveZone(): void {
    if (!this.canSave) {
      this.toastr.error('Choose a contact, name the zone, and select ' + this.boundaryCodePluralLabel);
      return;
    }

    this.saving = true;
    this.api.save({
      zone_type: this.zoneType,
      contact_uuid: this.selectedContactUuid,
      zone_uuid: this.editingZoneUuid || undefined,
      zone_name: this.zoneName.trim(),
      zone_id: this.zoneId.trim(),
      country: this.boundaryCountry,
      description: this.description.trim(),
      postal_codes: this.selectedZipList,
      gap_fill_miles: this.normalizedGapFillMiles(),
      clean_hole_square_miles: this.normalizedCleanHoleSquareMiles()
    }).pipe(
      finalize(() => {
        this.saving = false;
      })
    ).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.persistLastContact();
        this.startNewZone();
        this.toastr.success(response.message);
        this.loadExistingZones(false);
      },
      error: error => {
        this.toastr.error(this.apiErrorMessage(error, 'Save failed'));
      }
    });
  }

  private restoreLastContact(): void {
    const snapshot = this.readLastContact();
    if (!snapshot) {
      return;
    }

    this.zoneType = snapshot.zoneType;
    this.boundaryCountry = snapshot.country;
    this.country = snapshot.country;
    this.selectedContactUuid = snapshot.contactUuid;
    this.uuidDraft = snapshot.contactUuid;
    this.contactSearchTerm = snapshot.contactLabel;
    this.selectedContact = snapshot.contactLabel
      ? { value: snapshot.contactUuid, label: snapshot.contactLabel }
      : null;
    this.contactOptions = [];
    this.contactError = '';
  }

  private persistLastContact(): void {
    if (!this.selectedContactUuid || !this.canUseBrowserStorage()) {
      return;
    }

    const snapshot: ZoneMakerLastContact = {
      zoneType: this.zoneType,
      contactUuid: this.selectedContactUuid,
      contactLabel: this.selectedContact?.label || this.contactSearchTerm.trim(),
      country: this.boundaryCountry
    };

    try {
      window.localStorage.setItem(this.lastContactStorageKey, JSON.stringify(snapshot));
    } catch {
      // Local storage can be unavailable in restricted browser contexts.
    }
  }

  private readLastContact(): ZoneMakerLastContact | null {
    if (!this.canUseBrowserStorage()) {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(this.lastContactStorageKey);
      if (!raw) {
        return null;
      }

      const value = JSON.parse(raw) as Partial<ZoneMakerLastContact>;
      const zoneType = value.zoneType === 'carrier' ? 'carrier' : 'customer';
      const country = this.toBoundaryCountry(value.country) || 'USA';
      const contactUuid = (value.contactUuid || '').toLowerCase();

      if (!this.uuidPattern.test(contactUuid)) {
        window.localStorage.removeItem(this.lastContactStorageKey);
        return null;
      }

      return {
        zoneType,
        country,
        contactUuid,
        contactLabel: (value.contactLabel || '').trim()
      };
    } catch {
      window.localStorage.removeItem(this.lastContactStorageKey);
      return null;
    }
  }

  private canUseBrowserStorage(): boolean {
    try {
      return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
      return false;
    }
  }

  private resetContact(): void {
    this.contactSearchTerm = '';
    this.uuidDraft = '';
    this.selectedContactUuid = '';
    this.selectedContact = null;
    this.contactOptions = [];
    this.contactError = '';
  }

  private applyLoadedZoneRegions(regionCodes: string[]): void {
    const normalizedCodes = regionCodes
      .map(regionCode => regionCode.trim().toUpperCase())
      .filter(regionCode => /^[A-Z]{2}$/.test(regionCode));

    if (normalizedCodes.length === 0) {
      return;
    }

    this.selectedStates.clear();
    normalizedCodes.forEach(regionCode => this.selectedStates.add(regionCode));
    this.syncSelectedStateList();
  }

  private clearZoneWorkspace(): void {
    this.existingZones = [];
    this.syncExistingZoneColors();
    this.editingZoneUuid = '';
    this.zoneName = '';
    this.zoneId = '';
    this.country = this.boundaryCountry;
    this.description = '';
    this.clearSelection();
    this.removeLayer('existingZonesLayer');
    this.removeLayer('activeZoneLayer');
  }

  private loadViewportZips(): void {
    if (!this.map || !this.zipLayer) {
      return;
    }

    if (this.selectedStateList.length === 0) {
      this.zipLoadRequestId++;
      this.zipLayer.clearLayers();
      this.loadedZipCodes = [];
      this.loadedZipCount = 0;
      this.zipsLimited = false;
      this.loadingZips = false;
      return;
    }

    const requestId = ++this.zipLoadRequestId;
    const country = this.boundaryCountry;
    const states = [...this.selectedStateList];
    const statesKey = states.join(',');
    const bounds = this.map.getBounds();
    this.loadingZips = true;

    this.api.getZips({
      west: bounds.getWest(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      north: bounds.getNorth(),
      zoom: this.map.getZoom(),
      states,
      country
    }).subscribe({
      next: response => {
        if (!this.isCurrentZipLoad(requestId, country, statesKey)) {
          return;
        }

        if (!response.success) {
          this.toastr.error(response.message || this.boundaryCodePluralLabel + ' failed to load');
          return;
        }

        const featureCollection = response.data && response.data.feature_collection;
        if (!featureCollection || featureCollection.type !== 'FeatureCollection') {
          this.toastr.error(this.boundaryCodePluralLabel + ' response was missing map features');
          return;
        }

        this.loadedZipCount = response.data.count;
        this.zipLimit = response.data.limit;
        this.zipsLimited = response.data.count >= response.data.limit;
        this.loadedZipCodes = this.extractPostalCodes(featureCollection);
        this.zipLayer?.clearLayers();
        this.zipLayer?.addData(featureCollection as GeoJsonObject);
        this.refreshZipLayerStyles();
        this.activeZoneLayer?.bringToFront();
        this.previewLayer?.bringToFront();
      },
      error: error => {
        if (!this.isCurrentZipLoad(requestId, country, statesKey)) {
          return;
        }

        this.toastr.error(this.apiErrorMessage(error, this.boundaryCodePluralLabel + ' failed to load'));
      },
      complete: () => {
        if (this.zipLoadRequestId === requestId) {
          this.loadingZips = false;
        }
      }
    });
  }

  private isCurrentZipLoad(requestId: number, country: ZoneMakerBoundaryCountry, statesKey: string): boolean {
    return requestId === this.zipLoadRequestId
      && country === this.boundaryCountry
      && statesKey === this.selectedStateList.join(',');
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    const apiError = error as { error?: { message?: string } | string; message?: string; status?: number };
    if (typeof apiError?.error === 'string' && apiError.error.trim() !== '') {
      return apiError.error;
    }

    if (typeof apiError?.error === 'object' && apiError.error?.message) {
      return apiError.error.message;
    }

    if (apiError?.message) {
      return apiError.message;
    }

    if (apiError?.status) {
      return fallback + ' (' + apiError.status + ')';
    }

    return fallback;
  }

  private bindZipFeature(feature: Feature<Geometry, GeoJsonProperties>, layer: L.Layer): void {
    const postalCode = this.featureString(feature, 'postal_code');
    if (!postalCode) {
      return;
    }

    layer.bindTooltip(postalCode, {
      sticky: true,
      className: 'zone-maker-tooltip'
    });

    layer.on('click', event => {
      L.DomEvent.stopPropagation(event);
      this.togglePostalCode(postalCode);
    });
  }

  private togglePostalCode(postalCode: string): void {
    if (this.selectedZips.has(postalCode)) {
      this.selectedZips.delete(postalCode);
    } else {
      this.selectedZips.add(postalCode);
    }

    this.syncSelectedZipList();
    this.refreshZipLayerStyles();
    this.previewStats = null;
  }

  private mergePostalCodes(postalCodes: string[], mode: PostalMergeMode): void {
    if (mode === 'replace') {
      this.selectedZips.clear();
        this.removeLayer('radiusSelectionLayer');
      this.removeLayer('previewLayer');
    }

    postalCodes.forEach(postalCode => {
      const normalized = this.normalizePostalCode(postalCode);
      if (normalized) {
        this.selectedZips.add(normalized);
      }
    });

    this.syncSelectedZipList();
    this.refreshZipLayerStyles();
    this.previewStats = null;
  }

  private syncSelectedZipList(): void {
    this.selectedZipList = Array.from(this.selectedZips).sort();
  }

  private syncSelectedStateList(): void {
    this.selectedStateList = Array.from(this.selectedStates).sort();
  }


  private normalizedCleanHoleSquareMiles(): number {
    if (!this.boundaryCleanupEnabled) {
      return 0;
    }

    const squareMiles = Number(this.cleanHoleSquareMiles);
    if (!Number.isFinite(squareMiles)) {
      return 0;
    }

    return Math.min(100, Math.max(0, squareMiles));
  }

  private normalizedGapFillMiles(): number {
    if (!this.boundaryCleanupEnabled) {
      return 0;
    }

    const miles = Number(this.gapFillMiles);
    if (!Number.isFinite(miles)) {
      return 0;
    }

    return Math.min(5, Math.max(0, miles));
  }

  private applyRadius(request: ZoneMakerRadiusRequest): void {
    this.radiusLoading = true;
    this.api.radiusSelect(request).subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message);
          return;
        }

        this.mergePostalCodes(response.data.postal_codes, this.radiusMergeMode);
        this.drawRadiusSelection(response.data.feature_collection, true);
        this.drawRadiusCircle(response.data.center, response.data.radius_miles);

        if (response.data.count >= response.data.limit) {
          this.toastr.warning('Radius result hit the 5,000 ' + this.boundaryCodeLabel + ' limit');
        } else {
          this.toastr.info(`${response.data.count} ${this.boundaryCodePluralLabel} matched the radius`);
        }
      },
      error: () => this.toastr.error('Radius selection failed'),
      complete: () => {
        this.radiusLoading = false;
      }
    });
  }

  private onMapClick(latlng: L.LatLng): void {
    this.setRadiusCenterFromMap(latlng);
  }

  private setRadiusCenterFromMap(latlng: L.LatLng): void {
    this.map?.panTo(latlng);
  }

  private drawExistingZones(collection: ZoneMakerFeatureCollection, fitBounds: boolean): void {
    this.removeLayer('existingZonesLayer');

    this.existingZonesLayer = L.geoJSON(collection as GeoJsonObject, {
      interactive: false,
      style: feature => {
        const color = this.zoneColorForFeature(feature);
        return {
          color,
          weight: 2,
          opacity: 0.88,
          fillColor: color,
          fillOpacity: 0.14
        };
      },
      onEachFeature: (feature, layer) => {
        const zoneName = this.featureString(feature, 'zone_name') || 'Zone';
        layer.bindTooltip(zoneName, { sticky: true, className: 'zone-maker-tooltip' });
      }
    }).addTo(this.requireMap());

    this.existingZonesLayer.bringToBack();
    if (fitBounds) {
      this.fitLayer(this.existingZonesLayer);
    }
  }

  private drawActiveZone(collection: ZoneMakerFeatureCollection, fitBounds: boolean, zoneName = 'Selected zone'): void {
    this.removeLayer('activeZoneLayer');

    this.activeZoneLayer = L.geoJSON(collection as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#f43f5e',
        weight: 6,
        opacity: 0.98,
        fillColor: '#f43f5e',
        fillOpacity: 0.2,
        dashArray: '12 5',
        lineCap: 'round',
        lineJoin: 'round'
      }
    }).addTo(this.requireMap());

    this.activeZoneLayer.bringToFront();
    if (fitBounds) {
      this.fitLayer(this.activeZoneLayer);
    }
  }

  private drawRadiusSelection(collection: ZoneMakerFeatureCollection, fitBounds: boolean): void {
    this.removeLayer('radiusSelectionLayer');

    this.radiusSelectionLayer = L.geoJSON(collection as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#0891b2',
        weight: 1,
        opacity: 0.8,
        fillColor: '#67e8f9',
        fillOpacity: 0.2
      }
    }).addTo(this.requireMap());

    if (fitBounds) {
      this.fitLayer(this.radiusSelectionLayer);
    }
  }

  private drawRadiusCircle(center: { lat: number; lng: number } | null, radiusMiles: number): void {
    this.radiusCircle?.remove();
    this.radiusCircle = undefined;

    if (!center) {
      return;
    }

    this.radiusCircle = L.circle([center.lat, center.lng], {
      interactive: false,
      radius: radiusMiles * this.metersPerMile,
      color: '#0f766e',
      weight: 2,
      opacity: 0.8,
      fillColor: '#5eead4',
      fillOpacity: 0.08
    }).addTo(this.requireMap());
  }

  private drawFocusZip(collection: ZoneMakerFeatureCollection): void {
    this.removeLayer('focusZipLayer');

    this.focusZipLayer = L.geoJSON(collection as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#e11d48',
        weight: 3,
        opacity: 0.95,
        fillColor: '#fb7185',
        fillOpacity: 0.18
      }
    }).addTo(this.requireMap());

    this.fitLayer(this.focusZipLayer);
  }

  private drawPreview(collection: ZoneMakerFeatureCollection, fitBounds: boolean): void {
    this.removeLayer('previewLayer');
    this.removeLayer('activeZoneLayer');

    this.previewLayer = L.geoJSON(collection as GeoJsonObject, {
      interactive: false,
      style: {
        color: '#2563eb',
        weight: 5,
        opacity: 0.98,
        fillColor: '#93c5fd',
        fillOpacity: 0.24,
        dashArray: '10 4',
        lineCap: 'round',
        lineJoin: 'round'
      },
      onEachFeature: (_feature, layer) => {
        layer.bindTooltip('Preview boundary', { sticky: true, className: 'zone-maker-tooltip' });
      }
    }).addTo(this.requireMap());

    this.previewLayer.bringToFront();
    if (fitBounds) {
      this.fitLayer(this.previewLayer);
    }
  }

  private refreshZipLayerStyles(): void {
    this.zipLayer?.eachLayer(layer => {
      const feature = (layer as GeoJsonLeafletLayer).feature;
      if (feature && layer instanceof L.Path) {
        layer.setStyle(this.zipStyle(feature));
      }
    });
  }

  private zipStyle(feature?: Feature<Geometry, GeoJsonProperties>): L.PathOptions {
    const postalCode = this.featureString(feature, 'postal_code');
    const selected = postalCode !== '' && this.selectedZips.has(postalCode);

    return {
      color: selected ? '#059669' : '#64748b',
      weight: selected ? 2 : 1,
      opacity: selected ? 0.95 : 0.55,
      fillColor: selected ? '#34d399' : '#f8fafc',
      fillOpacity: selected ? 0.34 : 0.08
    };
  }

  private syncExistingZoneColors(): void {
    this.existingZoneColors.clear();
    this.existingZones.forEach((zone, index) => {
      const color = this.zonePalette[index % this.zonePalette.length];
      this.registerExistingZoneColor(zone.zone_uuid, color);
      this.registerExistingZoneColor(zone.zone_name, color);
    });
  }

  private registerExistingZoneColor(value: string | null | undefined, color: string): void {
    const key = (value || '').trim();
    if (key) {
      this.existingZoneColors.set(key, color);
    }
  }

  private zoneColorForFeature(feature: Feature<Geometry, GeoJsonProperties> | undefined): string {
    return this.colorForExistingZoneKeys(
      this.featureString(feature, 'zone_uuid'),
      this.featureString(feature, 'zone_name')
    );
  }

  private colorForExistingZoneKeys(zoneUuid: string | null | undefined, zoneName: string | null | undefined): string {
    const uuidKey = (zoneUuid || '').trim();
    const nameKey = (zoneName || '').trim();

    if (uuidKey && this.existingZoneColors.has(uuidKey)) {
      return this.existingZoneColors.get(uuidKey) || this.zonePalette[0];
    }

    if (nameKey && this.existingZoneColors.has(nameKey)) {
      return this.existingZoneColors.get(nameKey) || this.zonePalette[0];
    }

    return this.zoneColor(uuidKey || nameKey || 'Zone');
  }

  private zoneColor(value: string): string {
    let hash = 0;
    for (let index = 0; index < value.length; index++) {
      hash = ((hash << 5) - hash) + value.charCodeAt(index);
      hash |= 0;
    }

    return this.zonePalette[Math.abs(hash) % this.zonePalette.length];
  }

  private extractPostalCodes(collection: ZoneMakerFeatureCollection): string[] {
    return collection.features
      .map(feature => this.featureString(feature, 'postal_code'))
      .filter(postalCode => postalCode !== '');
  }

  private featureString(feature: Feature<Geometry, GeoJsonProperties> | undefined, key: string): string {
    const properties = feature?.properties;
    if (!properties) {
      return '';
    }

    const value = properties[key];
    return value === null || value === undefined ? '' : String(value);
  }

  private normalizePostalCode(postalCode: string): string {
    const clean = postalCode.toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (this.boundaryCountry === 'CAN') {
      const fsa = clean.slice(0, 3);
      return /^[A-Z][0-9][A-Z]$/.test(fsa) ? fsa : '';
    }

    const digits = clean.replace(/\D/g, '');
    return digits.length === 5 ? digits : '';
  }

  private toBoundaryCountry(country: string | null | undefined): ZoneMakerBoundaryCountry | null {
    const clean = (country || '').trim().toUpperCase();
    if (clean === 'CAN' || clean === 'CA') {
      return 'CAN';
    }

    if (clean === 'USA' || clean === 'US') {
      return 'USA';
    }

    return null;
  }

  private setBoundaryCountry(country: ZoneMakerBoundaryCountry, resetMap: boolean): void {
    this.boundaryCountry = country;
    this.country = country;
    this.stateOptions = [];
    this.selectedStates.clear();
    this.syncSelectedStateList();
    this.loadedZipCodes = [];
    this.loadedZipCount = 0;
    this.zipsLimited = false;
    this.zipSearchTerm = '';
    this.zipSearchResults = [];
    this.radiusCenterZip = '';
    this.zipLoadRequestId++;
    this.loadingZips = false;
    this.zipLayer?.clearLayers();
    this.clearSelection();
    this.removeLayer('focusZipLayer');
    this.loadStates();

    if (resetMap) {
      const center = country === 'CAN' ? L.latLng(56.1304, -106.3468) : L.latLng(39.5, -98.35);
      this.map?.setView(center, country === 'CAN' ? 4 : 5);
    }
  }

  private currentZoom(): number {
    return this.map?.getZoom() ?? 5;
  }

  private existingZonesOverviewZoom(): number {
    return Math.min(this.currentZoom(), 7);
  }

  private fitLayer(layer: L.GeoJSON): void {
    const bounds = layer.getBounds();
    if (bounds.isValid()) {
      this.map?.fitBounds(bounds.pad(0.18), { maxZoom: 11 });
    }
  }

  private requireMap(): L.Map {
    if (!this.map) {
      throw new Error('Map is not ready');
    }

    return this.map;
  }

  private removeLayer(layerName: 'existingZonesLayer' | 'activeZoneLayer' | 'radiusSelectionLayer' | 'focusZipLayer' | 'previewLayer'): void {
    const layer = this[layerName];
    if (layer) {
      layer.remove();
      this[layerName] = undefined;
    }
  }
}
